import { useMemo } from "react"

import type { InboxState } from "@/lib/states/global-state"
import { useLastKnown } from "@/lib/hooks/use-last-known"
import { useTimeTick } from "@/lib/hooks/use-time-tick"
import { useZeroWorkspace } from "@/lib/zero"
import { useSlaFeatureEnabled } from "../../threads/hooks/use-sla-feature-enabled"
import { useThreadCount } from "../useThreadCount"
import { closeLoopInMainQueue } from "./close-loop-queue"
import { useThreadTabs } from "./use-inbox-tabs"

const SLA_TICK_INTERVAL_MS = 5 * 60 * 1000

/**
 * The inbox tabs with their counts, shared by the inbox panel and the combined
 * sidebar. `everyTab` keeps SLA breached and Close loop at zero, for the
 * sidebar, whose visibility settings decide when they show.
 */
export function useInboxNavTabs(
	inboxState: InboxState,
	{ everyTab = false }: { everyTab?: boolean } = {},
) {
	const { count: newThreadsCountNum } = useThreadCount({ tab: "new" })
	const { count: assignedToMeCountNum } = useThreadCount({ tab: "my" })
	const { count: openCount } = useThreadCount({ tab: "open" })
	const { count: closeLoopCount } = useThreadCount({ tab: "close-loop" })
	// Workspace flags fall back to last session's values while Zero loads, so
	// the tab list draws in its final shape instead of reshuffling.
	const workspace = useZeroWorkspace()
	const closeLoopAutoSend = useLastKnown(
		"close-loop-auto-send",
		workspace ? !!workspace.aiChatSettings?.closeLoopAutoSend : undefined,
		false,
	)

	const slaEnabledLive = useSlaFeatureEnabled()
	const slaEnabled = useLastKnown(
		"sla-enabled",
		workspace ? slaEnabledLive : undefined,
		false,
	)
	const slaTick = useTimeTick(SLA_TICK_INTERVAL_MS)
	const slaNowMs = useMemo(
		() =>
			slaEnabled
				? Math.floor(Date.now() / SLA_TICK_INTERVAL_MS) * SLA_TICK_INTERVAL_MS
				: undefined,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[slaEnabled, slaTick],
	)
	const { count: slaBreachedCountLive, isLoaded: slaCountLoaded } =
		useThreadCount({
			tab: "sla-breached",
			nowMs: slaNowMs,
			enabled: slaEnabled,
			// Each 5-minute bucket is a new query, so a longer TTL keeps stale ones hydrated.
			ttl: "5m",
		})
	const slaBreachedCount = useLastKnown(
		"sla-breached-count",
		slaCountLoaded ? slaBreachedCountLive : undefined,
		0,
	)

	const tabs = useThreadTabs({
		openCount,
		newCount: newThreadsCountNum,
		assignedToMeCount: assignedToMeCountNum,
		closeLoopCount,
		slaBreachedCount,
		includeSlaBreached: slaEnabled,
		keepEmptySlaBreached: everyTab,
		includeCloseLoop:
			everyTab ||
			closeLoopInMainQueue(
				closeLoopAutoSend,
				closeLoopCount,
				inboxState === "close-loop",
			),
	})
	return { tabs }
}
